import { useEffect, useRef, useState } from 'react';
import { useCopilot } from '../../api/copilot';
import AudioUnlockBanner from './AudioUnlockBanner';
import MicButton from './MicButton';
import SpeakerToggle from './SpeakerToggle';
import { callSafely } from './util';

/* How long after the last keystroke the mic shows as listening again — Ki_UI's
   figure, long enough to cover the pause between two typed words. */
const TYPING_REST_MS = 2000;
/* Send turns into Stop the moment a question goes out, under the same pointer.
   For this long it stays inert, so the second click of a double-click cannot
   stop the answer the first click asked for. */
const STOP_GRACE_MS = 900;
const NO_CHIPS = [];

/**
 * Where a question is written or spoken: the text field, the mic, the voice
 * replies switch and send — plus optional quick chips above them.
 *
 * Enter sends. An IME composition's Enter is the reader choosing a character,
 * not finishing the question, so it is left alone.
 *
 * Send becomes Stop while the agent is answering and nothing has been typed:
 * that is the moment someone wants to cut a long spoken answer short, and a
 * second button beside send would crowd a column that is 300 px at its
 * narrowest. As soon as a follow-up is being typed the button is Send again,
 * because asking the next thing also interrupts.
 *
 * Questions go out while the assistant is still connecting too — the provider
 * queues one and sends it when the agent arrives — so neither the field nor
 * send is disabled by the connection.
 */
export default function CopilotComposer({ placeholder = 'Ask anything…', chips = NO_CHIPS }) {
  const {
    ask, stop, unlockAudio, listening = false, thinking = false, speaking = false, speaker = 'auto',
  } = useCopilot();
  const [draft, setDraft] = useState('');
  const [typing, setTyping] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const typingTimer = useRef(null);
  const sentTimer = useRef(null);

  /* The timers must not outlive the composer, or fire into an unmounted one. */
  useEffect(() => () => {
    clearTimeout(typingTimer.current);
    clearTimeout(sentTimer.current);
  }, []);
  /* A mic switched off while typing has nothing left to pause. */
  useEffect(() => {
    if (!listening) {
      clearTimeout(typingTimer.current);
      setTyping(false);
    }
  }, [listening]);

  const send = (text) => {
    const fromDraft = text == null;
    const q = String(fromDraft ? draft : text).trim();
    if (!q) return;
    /* A typed question answered aloud needs the browser's permission to play,
       and this click is the gesture that grants it. */
    if (speaker === 'on') callSafely(unlockAudio);
    callSafely(ask, q, { via: 'text' });
    /* A chip is its own question; it does not throw away one half-typed. */
    if (fromDraft) setDraft('');
    clearTimeout(typingTimer.current);
    setTyping(false);
    setJustSent(true);
    clearTimeout(sentTimer.current);
    sentTimer.current = setTimeout(() => setJustSent(false), STOP_GRACE_MS);
  };

  const onType = (value) => {
    setDraft(value);
    if (!listening) return;
    setTyping(true);
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => setTyping(false), TYPING_REST_MS);
  };

  const quick = Array.isArray(chips) ? chips : NO_CHIPS;
  const answering = thinking || speaking;
  const empty = !draft.trim();
  const canStop = answering && empty;

  return (
    <div className="cp-composer">
      <AudioUnlockBanner />
      {quick.length > 0 && (
        <div className="chips cp-chips" role="group" aria-label="Suggested questions">
          {quick.map((q) => (
            <button type="button" key={q} onClick={() => send(q)}>{q}</button>
          ))}
        </div>
      )}
      <div className="chatpane-input cp-input">
        <input
          value={draft}
          placeholder={listening && !typing ? 'Listening — speak, or type…' : placeholder}
          aria-label="Message the assistant"
          enterKeyHint="send"
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => {
            /* keyCode 229 is Safari's composition Enter, which it reports
               without setting isComposing. */
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing || e.keyCode === 229) return;
            e.preventDefault();
            send();
          }}
        />
        <SpeakerToggle />
        <MicButton paused={typing} />
        {canStop ? (
          <button
            type="button"
            className="send cp-stop"
            title="Stop the answer"
            aria-label="Stop the answer"
            onClick={() => {
              if (!justSent) callSafely(stop);
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" fill="currentColor" />
            </svg>
          </button>
        ) : (
          <button
            type="button"
            className="send"
            title="Send"
            aria-label="Send"
            /* aria-disabled, not disabled: a disabled button drops keyboard
               focus the instant a question is sent from it. */
            aria-disabled={empty}
            onClick={() => send()}
          >
            ↑
          </button>
        )}
      </div>
    </div>
  );
}
